/**
 * Creator Wizard Page
 * Main page for the 6-stage creator workflow
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { creatorWorkflowService } from '../services/creatorWorkflowService';
import type { CourseDraft } from '../types/creator-workflow';
import WorkflowProgress from '../components/creator-workflow/WorkflowProgress';
import StageContent from '../components/creator-workflow/StageContent';
import ResourceModalFlow from '../components/creator-workflow/resource-modal/ResourceModalFlow';
import WizardLandingState from '../components/creator-workflow/resource-modal/WizardLandingState';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import AccessDenied from '../components/AccessDenied';
import { scrollToBottom } from '../utils/scrollUtils';
import './CreatorWizardPage.css';

const CreatorWizardPage: React.FC = () => {
  const { t } = useTranslation(['creator', 'common']);
  const { draftId } = useParams<{ draftId?: string }>();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<CourseDraft | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState<boolean>(false);
  const [accessDeniedReason, setAccessDeniedReason] = useState<'role' | 'ownership' | 'unknown'>('unknown');
  const [autoSaving, setAutoSaving] = useState<boolean>(false);
  const previousStageRef = useRef<string | null>(null);

  // Ref to always have the latest draft (to avoid stale closures in callbacks)
  const draftRef = useRef<CourseDraft | null>(null);

  // Wrapper to update both state and ref synchronously
  const setDraftWithRef = useCallback((newDraft: CourseDraft | null | ((prev: CourseDraft | null) => CourseDraft | null)) => {
    if (typeof newDraft === 'function') {
      setDraft(prev => {
        const result = newDraft(prev);
        draftRef.current = result;
        return result;
      });
    } else {
      draftRef.current = newDraft;
      setDraft(newDraft);
    }
  }, []);

  // Performance: Skip localStorage backup during polling to avoid expensive JSON.stringify
  const isPollingRef = useRef<boolean>(false);

  // Global processing state - survives component unmounts (e.g., stage transitions)
  const [globalProcessing, setGlobalProcessing] = useState<boolean>(false);
  const [globalProcessingMessage, setGlobalProcessingMessage] = useState<string>('Processing...');

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  // "Edit a published course" mode — shows a top banner + Republish/Cancel
  // CTAs across every stage so the creator can finalize from the conversational
  // editor (or any other stage) without having to navigate to /subtitles.
  const [republishing, setRepublishing] = useState<boolean>(false);
  const isEditingPublished = !!draft?.editing_published_course_id;

  // Resource modal state for new drafts / material_input stage
  const [showResourceModal, setShowResourceModal] = useState<boolean>(false);

  // Save draft to localStorage whenever it changes (防止錯誤時數據丟失)
  // Performance: Skip during polling to avoid expensive JSON.stringify every second
  useEffect(() => {
    if (draft && !isPollingRef.current) {
      localStorage.setItem('creator_draft_backup', JSON.stringify(draft));
    }
  }, [draft]);

  // Load draft on mount or redirect to drafts page if no draftId
  useEffect(() => {
    // TEMPORARILY DISABLED: Check for authentication
    // const token = localStorage.getItem('accessToken');
    // if (!token) {
    //   setError('請先登入才能使用 Creator Wizard');
    //   setTimeout(() => navigate('/login'), 2000);
    //   return;
    // }

    if (draftId) {
      loadDraft(draftId);
    } else {
      // Redirect to drafts page when no draftId is provided
      // /creator/wizard is for editing a specific draft, not listing
      navigate('/creator/drafts', { replace: true });
    }
    // loadDraft is a plain function that depends on draftId (already in deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, navigate]);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (!draft) return;

    const autoSaveInterval = setInterval(() => {
      handleAutoSave();
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Auto-scroll when stage changes (e.g., after audio generation completes)
  // Also close global processing overlay when stage changes
  useEffect(() => {
    if (!draft) return;

    const currentStage = draft.current_stage;
    const previousStage = previousStageRef.current;

    // Only scroll if stage actually changed (not initial load)
    if (previousStage && previousStage !== currentStage) {
      console.log(`🎬 Stage changed: ${previousStage} → ${currentStage}, scrolling to bottom`);

      // Close global processing overlay when stage changes
      // This ensures overlay persists through the stage transition
      if (globalProcessing) {
        console.log('🌐 Auto-closing global processing overlay after stage change');
        setGlobalProcessing(false);
      }

      setTimeout(() => {
        scrollToBottom();
      }, 300); // Small delay to let UI update first
    }

    // Update the ref for next comparison
    previousStageRef.current = currentStage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.current_stage, globalProcessing]);

  // Auto-open resource modal for new drafts or drafts at material_input without a source
  useEffect(() => {
    if (!draft) return; // Still loading, don't show modal yet
    const shouldShow = draft.current_stage === 'material_input' && !draft.material_source;
    setShowResourceModal(shouldShow);
    // Intentionally only triggered on draft ID change, not on every draft property change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id]);

  const loadDraft = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      setIsAccessDenied(false);
      setAccessDeniedReason('unknown');
      const data = await creatorWorkflowService.getDraft(id);
      setDraftWithRef(data);
    } catch (err: any) {
      // Check for 403 Forbidden error
      if (err.response?.status === 403 || err.message?.includes('403')) {
        setIsAccessDenied(true);
        // Determine if it's an ownership issue or role issue
        const errorMessage = err.response?.data?.message || err.message || '';
        if (errorMessage.includes('do not have access') || errorMessage.includes('not belong')) {
          setAccessDeniedReason('ownership');
        } else {
          setAccessDeniedReason('role');
        }
        return;
      }
      // If draft not found (404), clear localStorage and reset
      if (err.response?.status === 404) {
        console.warn('⚠️ Draft not found in database, clearing localStorage');
        clearAllDraftData();
        setError('Draft not found. Starting fresh.');
      } else {
        setError(err.response?.data?.message || 'Failed to load draft');
      }
      console.error('Load draft error:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearAllDraftData = () => {
    // Clear all draft-related data from localStorage
    localStorage.removeItem('creator_draft_backup');
    localStorage.removeItem('stage1_form_backup');
    localStorage.removeItem('stage2_form_backup');

    // Clear all stage2 edit backups (they have draft ID in the key)
    Object.keys(localStorage)
      .filter(key => key.startsWith('stage2_edit_backup_'))
      .forEach(key => localStorage.removeItem(key));

    setDraftWithRef(null);
    console.log('🧹 已清除所有草稿相關的 localStorage 數據');
  };

  const handleAutoSave = async () => {
    if (!draft) return;

    try {
      setAutoSaving(true);
      await creatorWorkflowService.updateDraft(draft.id, {
        title: draft.title,
        description: draft.description,
        tags: draft.tags,
      });
    } catch (err) {
      console.error('Auto-save error:', err);
    } finally {
      setAutoSaving(false);
    }
  };

  const handleUpdateDraft = useCallback(async (data: any, draftId?: string) => {
    const targetDraftId = draftId || draft?.id;
    if (!targetDraftId) return;

    try {
      setLoading(true);
      setError(null);
      const updated = await creatorWorkflowService.updateDraft(targetDraftId, data);
      setDraftWithRef(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update draft');
      console.error('Update draft error:', err);
    } finally {
      setLoading(false);
    }
  }, [draft, setDraftWithRef]);

  const handleCreateDraft = async (data: {
    cefr_level: string;
    content_type: string;
    title?: string;
    description?: string;
    tags?: string[];
    target_word_count?: number;
  }): Promise<typeof draft> => {
    try {
      setLoading(true);
      setError(null);

      // Save current scroll position
      const scrollY = window.scrollY;

      const newDraft = await creatorWorkflowService.createDraft(data as any);
      setDraftWithRef(newDraft);

      // Update URL with draft ID
      navigate(`/creator/wizard/${newDraft.id}`, { replace: true });

      // Restore scroll position after navigation
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        window.scrollTo(0, scrollY);
      }, 0);

      // Return the new draft so caller can use it immediately
      return newDraft;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create draft');
      console.error('Create draft error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateContent = async (data: any, draftId?: string) => {
    const targetDraftId = draftId || draft?.id;
    if (!targetDraftId) return;

    try {
      setLoading(true);
      setError(null);
      const updated = await creatorWorkflowService.generateContent(targetDraftId, data);
      setDraftWithRef(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate content');
      console.error('Generate content error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleParseContent = async () => {
    console.log('🔵 handleParseContent called');
    console.log('🔵 draft:', draft);

    if (!draft) {
      console.log('❌ No draft found, returning');
      return;
    }

    try {
      console.log('🟢 Starting parse, draft.id:', draft.id);
      setLoading(true);
      setError(null);
      const updated = await creatorWorkflowService.parseContent(draft.id);
      console.log('✅ Parse completed, updated draft:', updated);
      console.log('📊 Vocabulary count:', updated.vocabulary?.length || 0);
      console.log('📊 Parsing status:', updated.parsing_status);
      console.log('📊 Stage progress:', updated.stage_progress?.parsing);
      setDraftWithRef(updated);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to parse content';
      console.error('❌ Parse content error:', errorMsg, err);
      setError(errorMsg);
    } finally {
      setLoading(false);
      console.log('🔵 handleParseContent finished');
    }
  };

  // ============================================================
  // SENTENCE EDITING HANDLERS
  // ============================================================

  const handleStartSentenceEditing = useCallback(async () => {
    if (!draft) return;
    try {
      setLoading(true);
      setError(null);
      const result = await creatorWorkflowService.startSentenceEditing(draft.id);
      // Reload full draft to get updated stage and sentences
      const updated = await creatorWorkflowService.getDraft(draft.id);
      setDraftWithRef(updated);
      console.log(`✅ Sentence editing started: ${result.sentenceCount} sentences`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start sentence editing');
      console.error('Start sentence editing error:', err);
    } finally {
      setLoading(false);
    }
  }, [draft, setDraftWithRef]);

  const handleUpdateSentenceEditing = useCallback(async (
    index: number,
    data: { text: string; speaker?: string | null },
  ) => {
    if (!draft) return;
    const result = await creatorWorkflowService.updateSentenceEditing(draft.id, index, data);
    // Optimistic update: patch the sentence in local draft
    setDraftWithRef(prev => {
      if (!prev) return prev;
      const newSentences = [...(prev.sentences || [])];
      (newSentences[index] as any) = {
        ...(newSentences[index] as any),
        text: result.sentence.text,
        speaker: result.sentence.speaker,
      };
      return { ...prev, sentences: newSentences };
    });
  }, [draft, setDraftWithRef]);

  const handleSplitSentenceEditing = useCallback(async (
    index: number,
    data: { firstText: string; secondText: string; secondSpeaker?: string | null },
  ) => {
    if (!draft) return;
    const result = await creatorWorkflowService.splitSentenceEditing(draft.id, index, data);
    setDraftWithRef(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        sentences: result.sentences.map(s => ({ ...(prev.sentences?.[s.index] as any), ...s })),
      };
    });
  }, [draft, setDraftWithRef]);

  const handleMergeNextSentenceEditing = useCallback(async (index: number) => {
    if (!draft) return;
    const result = await creatorWorkflowService.mergeNextSentenceEditing(draft.id, index);
    setDraftWithRef(prev => {
      if (!prev) return prev;
      return { ...prev, sentences: result.sentences as any[] };
    });
  }, [draft, setDraftWithRef]);

  const handleDeleteSentenceEditing = useCallback(async (index: number) => {
    if (!draft) return;
    const result = await creatorWorkflowService.deleteSentenceEditing(draft.id, index);
    setDraftWithRef(prev => {
      if (!prev) return prev;
      return { ...prev, sentences: result.sentences as any[] };
    });
  }, [draft, setDraftWithRef]);

  const handleAddSentenceEditing = useCallback(async (
    data: { afterIndex?: number | null; text: string; speaker?: string | null },
  ) => {
    if (!draft) return;
    const result = await creatorWorkflowService.addSentenceEditing(draft.id, data);
    setDraftWithRef(prev => {
      if (!prev) return prev;
      return { ...prev, sentences: result.sentences as any[] };
    });
  }, [draft, setDraftWithRef]);

  const handleGenerateAudio = async (options?: any) => {
    if (!draft) return;

    try {
      setLoading(true);
      setError(null);
      const updated = await creatorWorkflowService.generateAudio(draft.id, options);
      setDraftWithRef(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate audio');
      console.error('Generate audio error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSegmentedAudio = async (options?: any) => {
    if (!draft) return;

    const totalSentences = draft.sentences?.length || 0;

    try {
      // Reset progress to 0% BEFORE showing loading overlay
      setDraftWithRef({
        ...draft,
        stage_progress: {
          ...draft.stage_progress,
          audio_generation: {
            started_at: new Date().toISOString(),
            completed: false,
            progress_percent: 0,
            current_item: 0,
            total_items: totalSentences,
            status_message: 'Initializing audio generation...',
          },
        },
      });

      setLoading(true);
      setError(null);
      isPollingRef.current = true; // Skip localStorage backup during generation

      // Step 1: Initialize session
      const session = await creatorWorkflowService.initAudioGeneration(draft.id, {
        voiceCharacterId: options?.voiceCharacterId,
        speakerVoiceMap: options?.speakerVoiceMap,
        ttsProvider: options?.ttsProvider || 'inworld',
      });

      console.log(`🚀 Audio session initialized: ${session.totalSentences} sentences`);

      // Step 2: Generate each sentence one by one
      for (let i = 0; i < session.totalSentences; i++) {
        const result = await creatorWorkflowService.generateSentenceAudio(draft.id, i, {
          voiceCharacterId: options?.voiceCharacterId,
          speakerVoiceMap: options?.speakerVoiceMap,
          ttsProvider: options?.ttsProvider || 'inworld',
          cefrLevel: options?.cefrLevel,
        });

        console.log(`🎙️ [${i + 1}/${session.totalSentences}] Generated: ${result.duration.toFixed(1)}s (${result.speaker})`);

        // Update UI progress directly — no polling needed
        setDraftWithRef((prev: any) => ({
          ...prev,
          stage_progress: {
            ...prev.stage_progress,
            audio_generation: {
              ...prev.stage_progress?.audio_generation,
              current_item: i + 1,
              total_items: session.totalSentences,
              progress_percent: Math.round(((i + 1) / session.totalSentences) * 90),
              status_message: `Generated sentence ${i + 1}/${session.totalSentences}`,
            },
          },
        }));
      }

      // Step 3: Merge all sentence audio files
      setDraftWithRef((prev: any) => ({
        ...prev,
        stage_progress: {
          ...prev.stage_progress,
          audio_generation: {
            ...prev.stage_progress?.audio_generation,
            progress_percent: 95,
            status_message: 'Merging audio files and generating subtitles...',
          },
        },
      }));

      const updated = await creatorWorkflowService.mergeSentenceAudio(draft.id);
      console.log('✅ Audio generation complete!');

      isPollingRef.current = false;
      setDraftWithRef(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate segmented audio');
      console.error('Generate segmented audio error:', err);
    } finally {
      isPollingRef.current = false;
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!draft) return;

    try {
      setLoading(true);
      setError(null);
      const response = await creatorWorkflowService.validateDraft(draft.id);
      setDraftWithRef(response.draft);

      // Show validation results in UI
      return response.validation;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to validate draft');
      console.error('Validate draft error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceStage = useCallback(async () => {
    // Use draftRef to always get the latest draft (avoids stale closure issues)
    const currentDraft = draftRef.current;
    console.log('🚀 handleAdvanceStage: CALLED! draft =', currentDraft?.id, 'stage =', currentDraft?.current_stage);
    if (!currentDraft) {
      console.log('🚀 handleAdvanceStage: No draft, returning early!');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🚀 handleAdvanceStage: Current stage before advance:', currentDraft.current_stage);
      const updated = await creatorWorkflowService.advanceStage(currentDraft.id);
      console.log('🚀 handleAdvanceStage: API returned stage:', updated.current_stage);
      console.log('🚀 handleAdvanceStage: Full updated draft:', updated);
      setDraftWithRef(updated);

      // Auto-scroll to bottom after advancing to next stage
      scrollToBottom(500); // Wait 500ms for UI to update
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to advance stage');
      console.error('Advance stage error:', err);
    } finally {
      setLoading(false);
    }
  }, [setDraftWithRef]);

  const handleGoBackStage = useCallback(async () => {
    if (!draft) return;

    try {
      setLoading(true);
      setError(null);
      const updated = await creatorWorkflowService.goBackStage(draft.id);
      setDraftWithRef(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to go back to previous stage');
      console.error('Go back stage error:', err);
    } finally {
      setLoading(false);
    }
  }, [draft, setDraftWithRef]);

  const handleNavigateToStage = useCallback(async (stage: any) => {
    if (!draft) return;

    try {
      setLoading(true);
      setError(null);
      const updated = await creatorWorkflowService.navigateToStage(draft.id, stage);
      setDraftWithRef(updated);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to navigate to stage');
      console.error('Navigate to stage error:', err);
    } finally {
      setLoading(false);
    }
  }, [draft, setDraftWithRef]);

  const handleRefreshDraft = useCallback(async () => {
    if (!draft) return;

    try {
      console.log('🔄 Refreshing draft...', draft.id);
      console.log('   Current stage before refresh:', draft.current_stage);
      const data = await creatorWorkflowService.getDraft(draft.id);
      console.log('✅ Draft refreshed successfully');
      console.log('   New stage from server:', data.current_stage);
      console.log('   Material source:', data.material_source);
      setDraftWithRef(data);
      console.log('🎯 Draft state updated, UI should re-render with stage:', data.current_stage);
    } catch (err: any) {
      console.error('❌ Failed to refresh draft:', err);
    }
  }, [draft, setDraftWithRef]);

  // Global processing control - used by child components for long-running operations
  // This ensures the loading overlay persists even if the stage component unmounts
  const handleSetGlobalProcessing = (isProcessing: boolean, message?: string) => {
    console.log('🌐 Global processing:', isProcessing, message || '');
    setGlobalProcessing(isProcessing);
    if (message) {
      setGlobalProcessingMessage(message);
    }
  };

  const handleDeleteDraftClick = () => {
    if (!draft) return;
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!draft) return;

    try {
      setLoading(true);
      await creatorWorkflowService.deleteDraft(draft.id);
      // Clear all localStorage data after successful deletion
      clearAllDraftData();
      // Reset loading and draft state before navigation
      setLoading(false);
      setDraftWithRef(null);
      setDeleteModalOpen(false);
      navigate('/creator/wizard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete draft');
      console.error('Delete draft error:', err);
      setLoading(false);
      setDeleteModalOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  // Atomic-swap the shadow draft's content back into the live course.
  const handleRepublish = async () => {
    if (!draft?.id) return;
    const confirmed = window.confirm(t('creator:workflow.editPublished.republishConfirm'));
    if (!confirmed) return;
    setRepublishing(true);
    // Stage components (e.g. StageConversationalEditing) persist edits via a
    // 500ms debounced PATCH. If the creator drags a waveform region and
    // immediately clicks Republish, the timer hasn't fired yet — republish
    // would read stale draft.sentences from the DB. Wait ≥ debounce window
    // + a margin so any pending PATCH lands before we read.
    await new Promise((resolve) => setTimeout(resolve, 800));
    try {
      const { courseId } = await creatorWorkflowService.republishDraft(draft.id);
      navigate(`/courses/${courseId}`);
    } catch (err: any) {
      console.error('Republish failed:', err);
      setError(err.response?.data?.message || t('creator:workflow.editPublished.republishFailed'));
      setRepublishing(false);
    }
  };

  // Discard the shadow draft entirely — live course is untouched.
  const handleCancelEditPublished = async () => {
    if (!draft?.id) return;
    const confirmed = window.confirm(t('creator:workflow.editPublished.cancelEditConfirm'));
    if (!confirmed) return;
    try {
      await creatorWorkflowService.cancelEditPublishedCourse(draft.id);
      navigate('/creator/courses?tab=published');
    } catch (err: any) {
      console.error('Cancel edit failed:', err);
      setError(err.response?.data?.message || t('creator:workflow.editPublished.cancelEditFailed'));
    }
  };

  if (loading && !draft) {
    return (
      <div className="creator-wizard-page loading">
        <div className="spinner"></div>
        <p>{t('common:loading')}</p>
      </div>
    );
  }

  // Show access denied message
  if (isAccessDenied) {
    return (
      <div className="creator-wizard-page">
        <AccessDenied reason={accessDeniedReason} />
      </div>
    );
  }

  return (
    <div className="creator-wizard-page">
      <header className="wizard-header">
        <div className="wizard-header-left">
          <h1>
            {isEditingPublished
              ? t('creator:workflow.editPublished.title')
              : t('creator:workflow.wizard.title')}
          </h1>
          {autoSaving && <span className="auto-save-indicator">{t('creator:workflow.wizard.autoSaving')}</span>}
        </div>
        {draft && !isEditingPublished && (
          <button
            className="btn-delete"
            onClick={handleDeleteDraftClick}
            disabled={loading}
          >
            {t('creator:workflow.wizard.deleteDraft')}
          </button>
        )}
        {draft && isEditingPublished && (
          <div className="edit-published-actions">
            <button
              className="btn-secondary"
              onClick={handleCancelEditPublished}
              disabled={republishing}
            >
              {t('creator:workflow.editPublished.cancelEdit')}
            </button>
            <button
              className="btn-primary"
              onClick={handleRepublish}
              disabled={republishing || autoSaving}
            >
              {republishing
                ? t('creator:workflow.editPublished.republishing')
                : t('creator:workflow.editPublished.republish')}
            </button>
          </div>
        )}
      </header>

      {isEditingPublished && (
        <div className="edit-mode-banner" role="status" aria-live="polite">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 9v4M12 17h.01" />
            <circle cx="12" cy="12" r="10" />
          </svg>
          <span>{t('creator:workflow.editPublished.banner')}</span>
        </div>
      )}

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {draft && (
        <WorkflowProgress
          currentStage={draft.current_stage}
          stageProgress={draft.stage_progress}
          draft={draft}
          onStageClick={handleNavigateToStage}
        />
      )}

      {showResourceModal && (
        <>
          <WizardLandingState />
          <ResourceModalFlow
            isOpen={showResourceModal}
            draft={draft}
            onCreateDraft={handleCreateDraft}
            onUpdateDraft={handleUpdateDraft}
            onGenerateContent={handleGenerateContent}
            onParseContent={handleParseContent}
            onAdvanceStage={handleAdvanceStage}
            onClose={() => setShowResourceModal(false)}
            onComplete={() => {
              setShowResourceModal(false);
              handleRefreshDraft();
            }}
            onSetGlobalProcessing={handleSetGlobalProcessing}
            onRefreshDraft={handleRefreshDraft}
          />
        </>
      )}

      <main className="wizard-content">
        <StageContent
          draft={draft}
          loading={loading && !showResourceModal}
          onCreateDraft={handleCreateDraft}
          onUpdateDraft={handleUpdateDraft}
          onGenerateContent={handleGenerateContent}
          onStartSentenceEditing={handleStartSentenceEditing}
          onParseContent={handleParseContent}
          onGenerateAudio={handleGenerateAudio}
          onGenerateSegmentedAudio={handleGenerateSegmentedAudio}
          onValidate={handleValidate}
          onAdvanceStage={handleAdvanceStage}
          onGoBackStage={handleGoBackStage}
          onRefreshDraft={handleRefreshDraft}
          onSetGlobalProcessing={handleSetGlobalProcessing}
          onDraftUpdate={setDraft}
          onUpdateSentenceEditing={handleUpdateSentenceEditing}
          onSplitSentenceEditing={handleSplitSentenceEditing}
          onMergeNextSentenceEditing={handleMergeNextSentenceEditing}
          onDeleteSentenceEditing={handleDeleteSentenceEditing}
          onAddSentenceEditing={handleAddSentenceEditing}
        />
      </main>

      {/* Global Loading Overlay - persists even when stage components unmount */}
      {globalProcessing && (
        <div className="global-loading-overlay">
          <div className="global-loading-content">
            <div className="spinner"></div>
            <p>{globalProcessingMessage}</p>
          </div>
        </div>
      )}

      {/* Republish overlay — covers the page while atomic swap runs */}
      {republishing && (
        <div className="global-loading-overlay">
          <div className="global-loading-content">
            <div className="spinner"></div>
            <p>{t('creator:workflow.editPublished.republishingDescription')}</p>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        title={t('creator:workflow.wizard.deleteTitle')}
        message={t('creator:workflow.wizard.deleteMessage')}
        itemName={draft?.title || t('creator:workflow.draftList.untitledDraft')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default CreatorWizardPage;
